// src/pages/WorkItemChatPage.js
//
// Universal Work Item Chat Page
// =============================
//
// ROUTE CONTRACT
// --------------
// This page is rendered under:
//
//   /work-items/:key/chat
//
// Examples:
//   /work-items/todo%3A123/chat
//   /work-items/leave%3A9223372036854775807/chat
//
// CURRENT ARCHITECTURE ROLE
// -------------------------
// This page is intentionally a THIN ROUTE SHELL.
//
// It should NOT own:
// - auth bootstrap
// - token refresh handling
// - soft logout / hard logout
// - socket connection lifecycle
// - chat fetching
// - mark-as-read / mark-as-seen logic
//
// Those concerns belong to:
// - AuthContext / apiClient / ProtectedRoute
// - WorkItemChatPanel
//
// WHY KEEP THIS PAGE THIN?
// ------------------------
// Your current architecture is cleaner when routing pages do only:
//
// 1) read route params
// 2) normalize route input
// 3) render deterministic navigation
// 4) provide layout space for the real feature component
//
// This keeps session/auth complexity out of route wrappers and avoids
// duplicating business logic between:
// - full-page chat
// - embedded/modal chat variants
//
// LAYOUT NOTE
// -----------
// The actual scrolling chat UI lives in WorkItemChatPanel.
// This page only gives it a bounded-height slot:
//
//   .wic-page  -> top-level page shell
//   .wic-body  -> flex area with bounded height for the panel
//
// ACCESSIBILITY NOTE
// ------------------
// We expose:
// - a visible title
// - deterministic back navigation
// - a details link
// - a fallback state when the route param is invalid or empty
//
// I18N NOTE
// ---------
// This route shell owns only page-level labels and navigation text.
// The actual chat thread, composer and message state labels are translated in
// WorkItemChatPanel. Route parameters and work item keys remain raw technical
// identifiers and must not be translated.

import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import WorkItemChatPanel from "../components/WorkItemChatPanel";

import "./WorkItemChatPage.css";

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Safely decode a route param that may already be decoded or may contain
 * malformed escape sequences.
 *
 * Why this helper exists:
 * - route params often arrive as encoded strings
 * - decodeURIComponent can throw on malformed input
 * - this page should fail gracefully rather than crash
 */
function safeDecodeRouteParam(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * Build the details route for the current work item.
 *
 * We keep this as a helper to:
 * - avoid repeating encodeURIComponent in JSX
 * - keep the route contract explicit in one place
 */
function buildWorkItemDetailsHref(workItemKey) {
  const key = String(workItemKey || "").trim();
  if (!key) return "/dashboard";

  return `/work-items/${encodeURIComponent(key)}`;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const WorkItemChatPage = () => {
  const { key } = useParams();
  const { t } = useTranslation("workItems");

  /**
   * Normalize the route param once per route change.
   */
  const workItemKey = useMemo(() => {
    return safeDecodeRouteParam(key);
  }, [key]);

  /**
   * Defensive empty-state guard.
   *
   * In normal routing flow, :key should always exist.
   * But keeping this guard is still useful:
   * - protects against malformed navigation
   * - avoids rendering the panel with an empty key
   */
  const hasValidKey = Boolean(workItemKey);

  /**
   * Deterministic navigation target for the details page.
   */
  const detailsHref = useMemo(() => {
    return buildWorkItemDetailsHref(workItemKey);
  }, [workItemKey]);

  /* ------------------------------------------------------------------------ */
  /* Invalid / missing key fallback                                           */
  /* ------------------------------------------------------------------------ */

  if (!hasValidKey) {
    return (
      <div
        className="wic-page wic-pad"
        role="main"
        aria-label={t("chatPage.aria.page")}
      >
        <div className="wic-topbar">
          <Link
            to="/dashboard"
            className="wic-workitem-link"
            title={t("chatPage.actions.backToDashboardTitle")}
          >
            {t("chatPage.actions.dashboardWithArrow")}
          </Link>

          <div className="wic-title">{t("chatPage.title")}</div>

          <div className="wic-subtitle">{t("chatPage.invalid.subtitle")}</div>

          <Link
            to="/dashboard"
            className="wic-details-link"
            title={t("chatPage.actions.backToDashboardTitle")}
          >
            {t("chatPage.actions.backWithArrow")}
          </Link>
        </div>

        <div className="wic-body">
          <div
            className="wic-invalid-state"
            role="alert"
            aria-live="polite"
          >
            <h2>{t("chatPage.invalid.title")}</h2>
            <p>{t("chatPage.invalid.description")}</p>
            <p>{t("chatPage.invalid.recoveryHint")}</p>

            <div style={{ marginTop: 16 }}>
              <Link to="/dashboard" className="wic-workitem-link">
                {t("chatPage.actions.goToDashboardWithArrow")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------------ */
  /* Normal render                                                            */
  /* ------------------------------------------------------------------------ */

  return (
    <div
      className="wic-page wic-pad"
      role="main"
      aria-label={t("chatPage.aria.page")}
    >
      {/* -------------------------------------------------------------------- */}
      {/* Page-level top bar                                                   */}
      {/* -------------------------------------------------------------------- */}
      {/* This page intentionally keeps navigation very predictable:            */}
      {/* - back to dashboard                                                  */}
      {/* - open the work item details page                                    */}
      {/* -------------------------------------------------------------------- */}
      <div className="wic-topbar">
        <Link
          to="/dashboard"
          className="wic-workitem-link"
          title={t("chatPage.actions.backToDashboardTitle")}
        >
          {t("chatPage.actions.dashboardWithArrow")}
        </Link>

        <div className="wic-title">{t("chatPage.title")}</div>

        {/* Show the exact work item key for orientation/debug visibility. */}
        <div className="wic-subtitle" title={workItemKey}>
          {workItemKey}
        </div>

        <Link
          to={detailsHref}
          className="wic-details-link"
          title={t("chatPage.actions.openDetailsTitle")}
        >
          {t("chatPage.actions.detailsWithArrow")}
        </Link>
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* Bounded-height content slot                                          */}
      {/* -------------------------------------------------------------------- */}
      {/* WorkItemChatPanel owns the actual chat behavior.                     */}
      {/* This page only provides the layout container.                        */}
      {/* -------------------------------------------------------------------- */}
      <div className="wic-body">
        <WorkItemChatPanel
          workItemKey={workItemKey}
          variant="page"
        />
      </div>
    </div>
  );
};

export default WorkItemChatPage;
